/**
 * Simplified Object.assign ponyfill
 * @param target
 * @returns {*}
 */
export function assign(target) {
  let i, k, obj;

  if(!target) target = {};

  for(i = 1; i < arguments.length; i++) {
    obj = arguments[i];
    if(!obj) continue;
    for(k in obj) if(obj.hasOwnProperty(k)) target[k] = obj[k];
  }

  return target;
}

//=============================================================================
/**
 * Mutates the array, so it doesn't contain the first occurrence of given item
 * @param {array} arr
 * @param {*} item
 * @returns {array} - the same array
 */
export function removeFromArray(arr, item) {
  const index = arr.indexOf(item);
  if(index !== -1) arr.splice(index, 1);
  return arr;
}

//=============================================================================
export function getElement(el) { return typeof el === "string" ? document.getElementById(el) : el; }

//=============================================================================
/**
 * Helper for templates to locate particular template place as DOM object
 * after rendering it to document.
 *
 * Usage:
 * ```js
 * const sentinel = new Sentinel();
 * myElement.innerHTML = myTemplate.parse("sometemplate", { someplace: sentinel.string });
 * const sentinelElement = sentinel.getReference();
 * sentinelElement.parentNode.insertBefore(myOtherElement, sentinelElement);
 * // myOtherElement got inserted where ##someplace## was in the template
 * ```
 *
 * @constructor
 */
export function Sentinel() {
  /**
   * Hopefully unique id
   * @type {string}
   * @private
   */
  this.id = `__$SENTINEL_${Sentinel.nextId++}`;

  /**
   * HTML for the sentinel element
   * @type {string}
   */
  this.string = `<span id='${this.id}' style="display: none !important;"></span>`;
}

/**
 * Returns the rendered element, or null, if its not present in document.
 * @returns {HTMLElement | null}
 */
Sentinel.prototype.getReference = function() {
  return document.getElementById(this.id);
};

/**
 * Removes the rendered element from document
 */
Sentinel.prototype.remove = function() {
  const ref = this.getReference();
  if(!ref) return;
  ref.parentNode.removeChild(ref);
};

Sentinel.nextId = 0;

//=============================================================================
const dummyEl = document.createElement("div");

/**
 * Returns array of all top level nodes resulting from parsing given html.
 * @param {string} html
 * @returns {Node[]}
 */
export function elementsFromHTML(html) {
  const out = [];
  dummyEl.innerHTML = html;
  while(dummyEl.firstChild) {
    out.push(dummyEl.firstChild);
    dummyEl.removeChild(dummyEl.firstChild);
  }
  return out;
}

//=============================================================================
/**
 * Reset elements style.display
 * @param {HTMLElement} el
 */
export function show(el) {
  el.style.display = "";
}

//=============================================================================
/**
 * Set elements style.display to `"none"`
 * @param {HTMLElement} el
 */
export function hide(el) {
  el.style.display = "none";
}

//=============================================================================

// helper for removeclass
function wordRegexp(word) {
  return new RegExp('(?:^|\\s)' + word + '(?:\\s|$)', 'g');
}

export let hasClass, addClass, removeClass, toggleClass;

if('classList' in document.documentElement) {
  hasClass = function(el, className) {
    if(!className) return;
    if(typeof el === "string") el = document.getElementById(el);
    return el.classList.contains(className);
  };
  addClass = function(el, className) {
    if(!className) return;
    if(typeof el === "string") el = document.getElementById(el);
    el.classList.add(className);
  };
  removeClass = function(el, className) {
    if(!className) return;
    if(typeof el === "string") el = document.getElementById(el);
    el.classList.remove(className);
  };
} else {
  hasClass = function(el, className) {
    if(!className) return;
    if(typeof el === "string") el = document.getElementById(el);
    return wordRegexp(className).test(el.className);
  };
  addClass = function(el, className) {
    if(!className) return;
    if(typeof el === "string") el = document.getElementById(el);
    !hasClass(el, className) && (el.className += ' ' + className);
  };
  removeClass = function(el, className) {
    if(!className) return;
    if(typeof el === "string") el = document.getElementById(el);
    el.className = el.className.replace(wordRegexp(className), ' ');
  };
}
// native solution doesn't work guess where
toggleClass = function(el, className, kam) {
  if(!className) return;
  if(typeof el === "string") el = document.getElementById(el);
  if(typeof kam === "undefined") kam = !hasClass(el, className);
  if(kam) addClass(el, className);
  else {
    removeClass(el, className);
  }
};
//=============================================================================
/**
 * Computes a diff of two sorted arrays
 * @param {array} arr1
 * @param {array} arr2
 * @param {function} [cmp] - function used to sort the arrays (`arr1.sort(cmp)`)
 * @returns {{removed: array, added: array, preserved: array}}
 */
export function sortedArrayDiff(arr1, arr2, cmp = (a, b) => a - b) {
  const removed = [], added = [], preserved = [];

  const l1 = arr1.length;
  const l2 = arr2.length;

  let i1 = 0, i2 = 0;
  for(; i1 < l1 && i2 < l2;) {
    const e1 = arr1[i1];
    const e2 = arr2[i2];

    const c = cmp(e1, e2);

    if(c > 0) {
      added.push(e2);
      i2++;
    } else if(c < 0) {
      removed.push(e1);
      i1++;
    } else {
      preserved.push(e1);
      i1++;
      i2++;
    }
  }

  while(i1 < l1) removed.push(arr1[i1++]);

  while(i2 < l2) added.push(arr2[i2++]);

  return { removed, added, preserved };
}
//=============================================================================
/**
 * Helper for listening to dragenter/dragleave on particular element.
 * Both event types bubble from descendant elements, so we need to count their
 * occurrences to determine, if the root element was entered or left.
 * @param {object} obj
 * @param {HTMLElement} obj.el
 * @param {function} obj.enter - dragenter callback
 * @param {function} obj.leave - dragleave callback
 */
export function onDrag({ el, enter, leave }) {
  let dragenters = 0;

  el.addEventListener("dragenter", evt => { if(dragenters++ === 0) enter(evt); });

  el.addEventListener("dragleave", evt => { if(--dragenters === 0) leave(evt); });

  el.addEventListener("drop", evt => { if(dragenters) leave(evt); dragenters = 0;  });
}
//=============================================================================
// https://developer.mozilla.org/en-US/docs/Web/API/Window/scrollX
export function scrollX() {
  return (window.pageXOffset !== undefined)
    ? window.pageXOffset
    : (document.documentElement || document.body.parentNode || document.body).scrollLeft;
}

export function scrollY() {
  return (window.pageYOffset !== undefined)
    ? window.pageYOffset
    : (document.documentElement || document.body.parentNode || document.body).scrollTop;
}
//=============================================================================
export function throttle(fn) {
  let args, thisArg;
  let scheduled = false;

  function callback() {
    fn.apply(thisArg, args);
    scheduled = false;
  }

  return function throttled() {
    args = [];
    for(let i = 0; i < arguments.length; i++) args.push(arguments[i]);
    thisArg = this;
    if(!scheduled) {
      requestAnimationFrame(callback);
      scheduled = true;
    }
  }
}
//=============================================================================
/**
 * Escape regexp string
 * @param {string} str
 * @returns {string}
 */
export function escapeRegExp(str) {
  return (str+'').replace(/[.?*+^$[\]\\(){}|-]/g, "\\$&");
}
//=============================================================================
/**
 * Helper for binding and unbinding multiple events on one element
 * It's main purpose is to avoid repetition when binding events temporarily.
 * @param {EventTarget} el
 * @param {object} obj - object of eventName: callback
 * @returns {function} unbindEvents - function which unbinds all bound listeners
 */
export function bindEvents(el, obj) {
  const events = Object.keys(obj);

  for(let i = 0; i < events.length; i++) {
    const event = events[i];
    el.addEventListener(event, obj[event]);
  }

  return function unbindEvents() {
    for(let i = 0; i < events.length; i++) {
      const event = events[i];
      el.removeEventListener(event, obj[event]);
    }
  }
}
